import type { ReactNode } from 'react';
import styles from './Tooltip.module.css';

/**
 * Figma: `Tooltip` (99:4758) — "Contextual text overlay triggered on hover or
 * focus. Supporting text: True | False — toggles a secondary description below
 * the title. Arrow: None | Bottom left | Bottom right | Bottom center | Top
 * center | Left | Right. Properties: Text (editable text content). Layout:
 * Vertical auto-layout. 14 total variants."
 *
 * The annotation says "Dark background with light text for high contrast", and
 * as of the 2026-09 update the bound tokens finally agree: `Content` fills with
 * `surface/inverse`, the title takes `text/inverse` and the supporting line
 * `text/inverse-secondary`. For months the bindings said the opposite and the
 * tokens were followed over the annotation; that contradiction is now resolved
 * in the annotation's favour. See plan.md §7 item 185.
 *
 * This is the bubble only, which is all the Figma component is. `HelpIcon`
 * anchors one to a trigger and owns the open/close behaviour; this owns how it
 * looks getting there — see `open`.
 */
export type TooltipArrow =
  | 'none'
  | 'bottomCenter'
  | 'bottomLeft'
  | 'bottomRight'
  | 'topCenter'
  | 'left'
  | 'right';

export type TooltipProps = {
  /** Figma `Text` — the title line. */
  children: ReactNode;
  /**
   * Figma `Supporting text`. Omit for the single-line variant, which centres its
   * title; supplying it switches both lines to left-aligned, as the design does.
   */
  supportingText?: string;
  /** Figma `Arrow`. The direction names the side the arrow sits on. */
  arrow?: TooltipArrow;
  /**
   * ARIA role. `tooltip` is right when the bubble describes a trigger, which is
   * every case in the design. Not in Figma. Pass `none` for a purely decorative
   * render — an all-variants story, say — so screen readers do not announce a
   * dozen orphaned tooltips.
   */
  role?: 'tooltip' | 'none';
  /**
   * Whether the bubble is showing. Not in Figma, which draws it open. A host that
   * keeps the bubble mounted through its exit flips this to `false` and unmounts
   * on the opacity `transitionend`; left at the default, a bubble simply appears
   * as it mounts, which is every static render.
   */
  open?: boolean;
  id?: string;
  className?: string;
};

const arrowClass: Record<TooltipArrow, string | null> = {
  none: null,
  bottomCenter: styles.bottomCenter,
  bottomLeft: styles.bottomLeft,
  bottomRight: styles.bottomRight,
  topCenter: styles.topCenter,
  left: styles.left,
  right: styles.right,
};

/** The arrow sits after the bubble on these sides and before it on the others. */
const ARROW_LAST: TooltipArrow[] = ['bottomCenter', 'bottomLeft', 'bottomRight', 'right'];

export function Tooltip({
  children,
  supportingText,
  arrow = 'none',
  role = 'tooltip',
  open = true,
  id,
  className,
}: TooltipProps) {
  const classes = [
    styles.tooltip,
    /* Figma FIXES every `Supporting text=True` frame at 280 — see the stylesheet. */
    supportingText !== undefined ? styles.withSupporting : null,
    arrowClass[arrow],
    open ? null : styles.hidden,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  /*
   * Figma draws the arrow as a 12x12 square rotated 45 degrees, half of it
   * buried in the bubble. `aria-hidden` because it carries no meaning.
   */
  const arrowNode = arrow === 'none' ? null : <span className={styles.arrow} aria-hidden="true" />;

  return (
    <div className={classes} id={id} role={role === 'tooltip' ? 'tooltip' : undefined}>
      {!ARROW_LAST.includes(arrow) && arrowNode}

      <div className={styles.bubble}>
        {supportingText === undefined ? (
          <span className={styles.label}>{children}</span>
        ) : (
          <span className={styles.text}>
            <span className={styles.title}>{children}</span>
            <span className={styles.supporting}>{supportingText}</span>
          </span>
        )}
      </div>

      {ARROW_LAST.includes(arrow) && arrowNode}
    </div>
  );
}
